import { BoxPlanningApi } from "@/modules/boxPlanning/gateway/boxPlanningApi"
import { BoxProgramApi } from "@/modules/boxProgrammation/gateway/boxProgramApi"
import type { BoxProgramApiGateway } from "@/modules/boxProgrammation/gateway/boxProgramApiGateway"
import { BoxProgramSlotApi } from "@/modules/boxProgrammation/gateway/boxProgramSlotApi"
import type { BoxProgramSlotApiGateway } from "@/modules/boxProgrammation/gateway/boxProgramSlotApiGateway"
import { BoxShopApi } from "@/modules/boxShop/gateway/boxShopApi"
import type { BoxShopApiGateway } from "@/modules/boxShop/gateway/boxShopApiGateway"
import { BoxShopSalesApi } from "@/modules/boxShop/gateway/boxShopSalesApi"
import type { BoxShopSalesApiGateway } from "@/modules/boxShop/gateway/boxShopSalesApiGateway"
import { BoxSlotApi } from "@/modules/boxSlot/gateway/boxSlotApi"
import type { BoxSlotApiGateway } from "@/modules/boxSlot/gateway/boxSlotApiGateway"
import { BoxSlotSessionApi } from "@/modules/boxSlotSession/gateway/boxSlotSessionApi"
import type { BoxSlotSessionApiGateway } from "@/modules/boxSlotSession/gateway/boxSlotSessionApiGateway"
import { BoxSlotTemplateApi } from "@/modules/boxSlotTemplate/gateway/boxSlotTemplateApi"
import type { BoxSlotTemplateApiGateway } from "@/modules/boxSlotTemplate/gateway/boxSlotTemplateApiGateway"
import { ChatApi } from "@/modules/chat/gateway/chatApi"
import { CoachApi } from "@/modules/coach/gateway/coachApi"
import { type CoachApiGateway } from "@/modules/coach/gateway/coachApiGateway"
import { CoachCategoriesApi } from "@/modules/coachCategories/gateway/coachCategoriesApi"
import { type CoachCategoriesApiGateway } from "@/modules/coachCategories/gateway/coachCategoriesApiGateway"
import { CoachOffersApi } from "@/modules/coachOffers/gateway/coachOffersApi"
import { type CoachOffersApiGateway } from "@/modules/coachOffers/gateway/coachOffersApi.gateway"
import { CoachProgrammationApi } from "@/modules/coachProgrammation/gateway/coachProgrammationApi"
import { type CoachProgrammationApiGateway } from "@/modules/coachProgrammation/gateway/coachProgrammationApi.gateway"
import { ServiceManager } from "@/modules/core/services/serviceManager"
import { PaymentsToValidateApi } from "@/modules/finances/paymentsToValidate/gateway/paymentsToValidateApi"
import type { PaymentsToValidateApiGateway } from "@/modules/finances/paymentsToValidate/gateway/paymentsToValidateApiGateway"
import { ScoreApi } from "@/modules/scores/scoreForms/gateway/scoreApi"
import { type ScoreApiGateway } from "@/modules/scores/scoreForms/gateway/scoreApiGateway"
import { ProgramInfiniteSessionApi } from "@/modules/sessions/gateway/programInfiniteSessionApi"
import type { ProgramInfiniteSessionApiGateway } from "@/modules/sessions/gateway/programInfiniteSessionApiGateway"
import { ProgramOnOffSessionApi } from "@/modules/sessions/gateway/programOnOffSessionApi"
import type { ProgramOnOffSessionApiGateway } from "@/modules/sessions/gateway/programOnOffSessionApiGateway"
import { SocialApi } from "@/modules/social/gateway/socialApi"
import type { SocialApiGateway } from "@/modules/social/gateway/socialApiGateway"
import { TemplateApi } from "@/modules/template/gateway/templateApi"
import type { TemplateApiGateway } from "@/modules/template/gateway/templateApiGateway"
import { TemplateCategoryApi } from "@/modules/template/gateway/templateCategoryApi"
import type { TemplateCategoryApiGateway } from "@/modules/template/gateway/templateCategoryApiGateway"
import { type TrainingApiGateway } from "@/modules/training/gateway/trainingApiGateway"
import { UserProfileApi } from "@/modules/users/gateway/usersApi"
import { type UserProfileApiGateway } from "@/modules/users/gateway/usersApiGateway"
import { ArticleApi } from "@modules/article/gateway/articleApi"
import { type ArticleApiGateway } from "@modules/article/gateway/articleApiGateway"
import { BenchmarkApi } from "@modules/benchmark/api/benchmarkApi"
import { type BenchmarkApiGateway } from "@modules/benchmark/api/benchmarkGateway"
import { BoxApi } from "@modules/box/services/boxApi"
import { type BoxApiGateway } from "@modules/box/services/boxApi.gateway"
import { BoxB2BProgramApi } from "@modules/boxB2BProgram/gateway/boxB2BProgramApi"
import { type BoxB2BProgramApiGateway } from "@modules/boxB2BProgram/gateway/boxB2BProgramApi.gateway"
import { BoxDashboardApi } from "@modules/boxDashboard/gateway/boxDashboardApi"
import { type BoxDashboardApiGateway } from "@modules/boxDashboard/gateway/boxDashboardApiGateway"
import { BoxLeadApi } from "@modules/boxLead/gateway/boxLeadApi"
import { type BoxLeadApiGateway } from "@modules/boxLead/gateway/boxLeadApiGateway"
import { OfferApi } from "@modules/boxOffer/gateway/boxOfferApi"
import { type BoxOfferApiGateway } from "@modules/boxOffer/gateway/boxOfferApi.gateway"
import { BoxPurchasesApi } from "@modules/boxPurchases/gateway/boxPurchasesApi"
import { type BoxPurchasesApiGateway } from "@modules/boxPurchases/gateway/boxPurchasesApiGateway"
import { BoxStaffApi } from "@modules/boxStaff/gateway/boxStaffApi"
import { type BoxStaffApiGateway } from "@modules/boxStaff/gateway/boxStaffApiGateway"
import { BoxSubscriptionApi } from "@modules/boxSubscription/gateway/boxSubscriptionApi"
import { type BoxSubscriptionApiGateway } from "@modules/boxSubscription/gateway/boxSubscriptionApiGateway"
import { MediaApi } from "@modules/common/media/api/mediaApi"
import { type MediaApiGateway } from "@modules/common/media/api/mediaApiGateway"
import { Config } from "@modules/config"
import { ApiClient } from "@modules/core/api/client/apiClient"
import { ApiService } from "@modules/core/services/apiService"
import { ConfigService } from "@modules/core/services/configService"
import { DateService } from "@modules/core/services/dateService"
import { CrossfitEventApi } from "@modules/crossfitEvent/api/crossfitEventApi"
import { type CrossfitEventApiGateway } from "@modules/crossfitEvent/api/crossfitEventGateway"
import { ExploreApi } from "@modules/explore/gateway/exploreApi"
import { FeatureToggleService } from "@modules/featureToggle/services/featureToggleService"
import { PayoutApi } from "@modules/finances/financesPayout/gateway/payoutApi"
import { type PayoutApiGateway } from "@modules/finances/financesPayout/gateway/payoutApiGateway"
import { BoxFinanceApi } from "@modules/finances/gateway/boxFinanceApi"
import { type BoxFinanceApiGateway } from "@modules/finances/gateway/boxFinanceApiGateway"
import { UserFinanceApi } from "@modules/finances/gateway/userFinanceApi"
import { type UserFinanceApiGateway } from "@modules/finances/gateway/userFinanceApiGateway"
import { HomeApi } from "@modules/home/gateway/homeApi"
import { type HomeApiGateway } from "@modules/home/gateway/homeApiGateway"
import { LanguageService } from "@modules/language/services/languageService"
import { MemberApi } from "@modules/members/gateway/memberApi"
import { type MemberApiGateway } from "@modules/members/gateway/memberApiGateway"
import { ObjectivesApi } from "@modules/objectives/services/objectivesApi"
import { ObjectivesService } from "@modules/objectives/services/objectivesService"
import { PaymentApi } from "@modules/payment/gateway/paymentApi"
import { PaymentService } from "@modules/payment/gateway/paymentService"
import { PublicProfileApi } from "@modules/profile/publicProfile/gateway/publicProfileApi"
import { type PublicProfileApiGateway } from "@modules/profile/publicProfile/gateway/publicProfileApi.gateway"
import { BrowserProfileService } from "@modules/profile/services/browserProfileService"
import { ProgramBundleApi } from "@modules/programBundle/api/programBundleApi"
import { type ProgramBundleApiGateway } from "@modules/programBundle/api/programBundleApiGateway"
import { ProgramBundleSubscriberApi } from "@modules/programBundle/programBundleSubscribers/gateway/programBundleSubscriberApi"
import { type ProgramBundleSubscriberApiGateway } from "@modules/programBundle/programBundleSubscribers/gateway/programBundleSubscriberApiGateway"
import { ProgramApi } from "@modules/programs/gateway/programApi"
import { type ProgramApiGateway } from "@modules/programs/gateway/programApiGateway"
import { ProgramInfiniteApi } from "@modules/programs/gateway/programInfiniteApi"
import { ProgramOnOffApi } from "@modules/programs/gateway/programOnOffApi"
import { ProgramSasApi } from "@modules/programs/programSas/gateway/programSasApi"
import { type ProgramSasApiGateway } from "@modules/programs/programSas/gateway/programSasApiGateway"
import { ProgramBoxSubscriberApi } from "@modules/programs/programSubscriber/gateway/programBoxSubscriberApi"
import { type ProgramBoxSubscriberApiGateway } from "@modules/programs/programSubscriber/gateway/programBoxSubscriberApiGateway"
import { ProgramSubscriberApi } from "@modules/programs/programSubscriber/gateway/programSubscriberApi"
import { type ProgramSubscriberApiGateway } from "@modules/programs/programSubscriber/gateway/programSubscriberApiGateway"
import { BoxPromoCodeApi } from "@modules/promoCode/gateway/boxPromoCodeApi"
import { type BoxPromoCodeApiGateway } from "@modules/promoCode/gateway/boxPromoCodeApi.gateway"
import { PromoCodeApi } from "@modules/promoCode/gateway/promoCodeApi"
import { type PromoCodeApiGateway } from "@modules/promoCode/gateway/promoCodeApi.gateway"
import { RemoteConfigService } from "@modules/remoteConfig/services/remoteConfigService"
import { ScoreboardApi } from "@modules/scores/scoreboard/gateway/scoreboardApi"
import type { ScoreboardApiGateway } from "@modules/scores/scoreboard/gateway/scoreboardApiGateway"
import { AssetApi } from "@modules/services/assetApi"
import { AssetService } from "@modules/services/assetService"
import { SharedCoachApi } from "@modules/sharedCoach/gateway/sharedCoachApi"
import { type SharedCoachApiGateway } from "@modules/sharedCoach/gateway/sharedCoachApiGateway"
import { LocalStorageService } from "@modules/storage/services/localStorageService"
import { WebStorage } from "@modules/storage/storage"
import { TrainingApi } from "@modules/training/gateway/trainingApi"

export const getAllServices = ({
  customApiClient,
  customUserProfileApi,
  customBoxApi,
  customChatApi,
  customMemberApi,
  customBoxOfferApi,
  customAssetApi,
  customProgramApi,
  customProgramBundleApi,
  customProgramBundleSubscriberApi,
  customProgramInfiniteApi,
  customProgramOnOffApi,
  customProgramSubscriberApi,
  customProgramBoxSubscriberApi,
  customProgramInfiniteSessionApi,
  customProgramOnOffSessionApi,
  customExploreApi,
  customArticleGateway,
  customScoreboardApi,
  customBenchmarkApi,
  customObjectivesApi,
  customBoxPlanningApi,
  customBoxSlotTemplateApi,
  customBoxB2BProgramApi,
  customBoxSlotApi,
  customPaymentApi,
  customBoxFinanceApi,
  customUserFinanceApi,
  customPaymentsToValidateApi,
  customBoxStaffApi,
  customTrainingApi,
  customScoreApi,
  customPromoCodeApi,
  customBoxPromoCodeApi,
  customCrossfitEventApi,
  customHomeApi,
  customMediaApi,
  customBoxSubscriptionApi,
  customBoxPurchasesApi,
  customPayoutApi,
  customBoxLeadApi,
  customPublicProfileApi,
  customProgramSasApi,
  customSharedCoachApi,
  customBoxDashboardApi,
  customBoxShopApi,
  customBoxShopSalesApi,
  customBoxSlotSessionApi,
  customBoxProgramApi,
  customBoxProgramSlotApi,
  customCoachApi,
  customCoachOffersApi,
  customCoachProgrammationApi,
  customCoachCategoriesApi,
  customTemplateApi,
  customTemplateCategoryApi,
  customSocialApi,
}: {
  customApiClient?: ApiClient
  customPublicProfileApi?: PublicProfileApiGateway
  customUserProfileApi?: UserProfileApiGateway
  customBoxApi?: BoxApiGateway
  customBoxProgramApi?: BoxProgramApiGateway
  customChatApi?: ChatApi
  customBoxOfferApi?: BoxOfferApiGateway
  customMemberApi?: MemberApiGateway
  customAssetApi?: AssetApi
  customProgramApi?: ProgramApiGateway
  customProgramInfiniteApi?: ProgramInfiniteApi
  customProgramOnOffApi?: ProgramOnOffApi
  customProgramBundleApi?: ProgramBundleApiGateway
  customProgramBundleSubscriberApi?: ProgramBundleSubscriberApiGateway
  customProgramSubscriberApi?: ProgramSubscriberApiGateway
  customProgramBoxSubscriberApi?: ProgramBoxSubscriberApiGateway
  customProgramInfiniteSessionApi?: ProgramInfiniteSessionApiGateway
  customProgramOnOffSessionApi?: ProgramOnOffSessionApiGateway
  customExploreApi?: ExploreApi
  customArticleGateway?: ArticleApiGateway
  customScoreboardApi?: ScoreboardApiGateway
  customBenchmarkApi?: BenchmarkApiGateway
  customObjectivesApi?: ObjectivesApi
  customBoxPlanningApi?: BoxPlanningApi
  customBoxSlotTemplateApi?: BoxSlotTemplateApiGateway
  customBoxB2BProgramApi?: BoxB2BProgramApiGateway
  customBoxSlotApi?: BoxSlotApiGateway
  customPaymentApi?: PaymentApi
  customBoxFinanceApi?: BoxFinanceApiGateway
  customUserFinanceApi?: UserFinanceApiGateway
  customPaymentsToValidateApi?: PaymentsToValidateApiGateway
  customTrainingApi?: TrainingApiGateway
  customScoreApi?: ScoreApiGateway
  customPromoCodeApi?: PromoCodeApiGateway
  customBoxPromoCodeApi?: BoxPromoCodeApiGateway
  customCrossfitEventApi?: CrossfitEventApiGateway
  customHomeApi?: HomeApiGateway
  customMediaApi?: MediaApiGateway
  customBoxPurchasesApi?: BoxPurchasesApiGateway
  customBoxSubscriptionApi?: BoxSubscriptionApiGateway
  customBoxStaffApi?: BoxStaffApiGateway
  customPayoutApi?: PayoutApiGateway
  customBoxLeadApi?: BoxLeadApiGateway
  customProgramSasApi?: ProgramSasApiGateway
  customSharedCoachApi?: SharedCoachApiGateway
  customBoxDashboardApi?: BoxDashboardApiGateway
  customBoxShopApi?: BoxShopApiGateway
  customBoxShopSalesApi?: BoxShopSalesApiGateway
  customTemplateApi?: TemplateApiGateway
  customTemplateCategoryApi?: TemplateCategoryApiGateway
  customBoxSlotSessionApi?: BoxSlotSessionApiGateway
  customBoxProgramSlotApi?: BoxProgramSlotApiGateway
  customCoachApi?: CoachApiGateway
  customCoachOffersApi?: CoachOffersApiGateway
  customCoachProgrammationApi?: CoachProgrammationApiGateway
  customCoachCategoriesApi?: CoachCategoriesApiGateway
  customSocialApi?: SocialApiGateway
} = {}) => {
  ConfigService.setConfig(Config)
  ConfigService.setDevMode(import.meta.env.DEV)

  /**
   * API
   */
  const apiClient = customApiClient ?? new ApiClient()
  const publicProfileApi = customPublicProfileApi ?? new PublicProfileApi(apiClient)
  const userProfileApi = customUserProfileApi ?? new UserProfileApi(apiClient)
  const boxApi = customBoxApi ?? new BoxApi(apiClient)
  const boxProgramApi = customBoxProgramApi ?? new BoxProgramApi(apiClient)
  const boxProgramSlotApi = customBoxProgramSlotApi ?? new BoxProgramSlotApi(apiClient)
  const chatApi = customChatApi ?? new ChatApi(apiClient)
  const memberApi = customMemberApi ?? new MemberApi(apiClient)
  const boxOfferApi = customBoxOfferApi ?? new OfferApi(apiClient)
  const assetApi = customAssetApi ?? new AssetApi(apiClient)
  const programApi = customProgramApi ?? new ProgramApi(apiClient)
  const programInfiniteApi = customProgramInfiniteApi ?? new ProgramInfiniteApi(apiClient)
  const programOnOffApi = customProgramOnOffApi ?? new ProgramOnOffApi(apiClient)
  const programBundleApi = customProgramBundleApi ?? new ProgramBundleApi(apiClient)
  const programSubscriberApi = customProgramSubscriberApi ?? new ProgramSubscriberApi(apiClient)
  const programBoxSubscriberApi = customProgramBoxSubscriberApi ?? new ProgramBoxSubscriberApi(apiClient)
  const programBundleSubscriberApi = customProgramBundleSubscriberApi ?? new ProgramBundleSubscriberApi(apiClient)
  const programInfiniteSessionApi = customProgramInfiniteSessionApi ?? new ProgramInfiniteSessionApi(apiClient)
  const programOnOffSessionApi = customProgramOnOffSessionApi ?? new ProgramOnOffSessionApi(apiClient)
  const exploreApi = customExploreApi ?? new ExploreApi(apiClient)
  const articleApi = customArticleGateway ?? new ArticleApi()
  const scoreboardApi = customScoreboardApi ?? new ScoreboardApi(apiClient)
  const benchmarkApi = customBenchmarkApi ?? new BenchmarkApi(apiClient)
  const objectivesApi = customObjectivesApi ?? new ObjectivesApi(apiClient)
  const boxPlanningApi = customBoxPlanningApi ?? new BoxPlanningApi(apiClient)
  const boxSlotTemplateApi = customBoxSlotTemplateApi ?? new BoxSlotTemplateApi(apiClient)
  const boxB2BProgramApi = customBoxB2BProgramApi ?? new BoxB2BProgramApi(apiClient)
  const boxSlotApi = customBoxSlotApi ?? new BoxSlotApi(apiClient)
  const paymentApi = customPaymentApi ?? new PaymentApi(apiClient)
  const boxFinanceApi = customBoxFinanceApi ?? new BoxFinanceApi(apiClient)
  const userFinanceApi = customUserFinanceApi ?? new UserFinanceApi(apiClient)
  const paymentsToValidateApi = customPaymentsToValidateApi ?? new PaymentsToValidateApi(apiClient)
  const boxStaffApi = customBoxStaffApi ?? new BoxStaffApi(apiClient)
  const trainingApi = customTrainingApi ?? new TrainingApi(apiClient)
  const scoreApi = customScoreApi ?? new ScoreApi(apiClient)
  const promoCodeApi = customPromoCodeApi ?? new PromoCodeApi(apiClient)
  const boxPromoCodeApi = customBoxPromoCodeApi ?? new BoxPromoCodeApi(apiClient)
  const crossfitEventApi = customCrossfitEventApi ?? new CrossfitEventApi(apiClient)
  const homeApi = customHomeApi ?? new HomeApi()
  const mediaApi = customMediaApi ?? new MediaApi(apiClient)
  const boxSubscriptionApi = customBoxSubscriptionApi ?? new BoxSubscriptionApi(apiClient)
  const boxPurchasesApi = customBoxPurchasesApi ?? new BoxPurchasesApi(apiClient)
  const payoutApi = customPayoutApi ?? new PayoutApi(apiClient)
  const boxLeadApi = customBoxLeadApi ?? new BoxLeadApi(apiClient)
  const programSasApi = customProgramSasApi ?? new ProgramSasApi(apiClient)
  const sharedCoachApi = customSharedCoachApi ?? new SharedCoachApi(apiClient)
  const boxDashboardApi = customBoxDashboardApi ?? new BoxDashboardApi(apiClient)
  const boxShopApi = customBoxShopApi ?? new BoxShopApi(apiClient)
  const boxShopSalesApi = customBoxShopSalesApi ?? new BoxShopSalesApi(apiClient)
  const templateApi = customTemplateApi ?? new TemplateApi(apiClient)
  const templateCategoryApi = customTemplateCategoryApi ?? new TemplateCategoryApi(apiClient)
  const boxSlotSessionApi = customBoxSlotSessionApi ?? new BoxSlotSessionApi(apiClient)
  const coachApi = customCoachApi ?? new CoachApi(apiClient)
  const coachOffersApi = customCoachOffersApi ?? new CoachOffersApi(apiClient)
  const coachProgrammationApi = customCoachProgrammationApi ?? new CoachProgrammationApi(apiClient)
  const coachCategoriesApi = customCoachCategoriesApi ?? new CoachCategoriesApi(apiClient)
  const socialApi = customSocialApi ?? new SocialApi(apiClient)
  /**
   * SERVICES
   */
  const localStorageService = new LocalStorageService(new WebStorage())
  const serviceManager = new ServiceManager(ConfigService.devMode)
  serviceManager.setActiveInstance(serviceManager)

  serviceManager.register(localStorageService)
  const featureToggleService = serviceManager.register(new FeatureToggleService(localStorageService))
  const languageService = serviceManager.register(
    new LanguageService(localStorageService, apiClient, featureToggleService),
  )
  const browserProfileService = serviceManager.register(new BrowserProfileService(localStorageService))
  const apiService = serviceManager.register(new ApiService(apiClient))
  const paymentService = serviceManager.register(new PaymentService(paymentApi))
  const objectivesService = serviceManager.register(new ObjectivesService(objectivesApi))
  const dateService = serviceManager.register(new DateService(languageService, featureToggleService))

  const remoteConfigService = serviceManager.register(new RemoteConfigService())

  // Outside service manager
  const assetService = new AssetService(assetApi)

  const allServices = {
    serviceManager,
    apiService,
    assetService,
    boxB2BProgramApi,
    dateService,
    featureToggleService,
    languageService,
    localStorageService,
    paymentService,
    browserProfileService,
    exploreApi,
    articleApi,
    trainingApi,
    scoreboardApi,
    programInfiniteApi,
    programOnOffApi,
    programSubscriberApi,
    programBoxSubscriberApi,
    programBundleSubscriberApi,
    programBundleApi,
    benchmarkApi,
    objectivesService,
    programInfiniteSessionApi,
    programOnOffSessionApi,
    scoreApi,
    remoteConfigService,
    crossfitEventApi,
    homeApi,
    mediaApi,
    boxSubscriptionApi,
    boxPurchasesApi,
    memberApi,
    boxStaffApi,
    boxApi,
    payoutApi,
    boxOfferApi,
    boxFinanceApi,
    userFinanceApi,
    paymentsToValidateApi,
    boxLeadApi,
    publicProfileApi,
    programSasApi,
    programApi,
    sharedCoachApi,
    paymentApi,
    promoCodeApi,
    boxPromoCodeApi,
    boxDashboardApi,
    boxShopApi,
    boxShopSalesApi,
    boxPlanningApi,
    boxSlotApi,
    boxSlotSessionApi,
    boxProgramApi,
    boxProgramSlotApi,
    boxSlotTemplateApi,
    userProfileApi,
    chatApi,
    coachApi,
    coachOffersApi,
    coachProgrammationApi,
    coachCategoriesApi,
    templateApi,
    templateCategoryApi,
    socialApi,
  } as const

  return allServices
}
